import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext.js";
import { api, ApiError } from "../../api/client.js";
import type { PersonaSummary } from "../../api/types.js";
import { RECOMMENDED_REFERENCE_VIEWS, REQUIRED_TRAINING_IMAGE_COUNT_LABEL } from "./persona-onboarding-constants.js";

interface PersonasTabProps {
  readonly tenantId: string;
}

/**
 * Persona onboarding UX hardening (2026-09-12): Create Persona is now its
 * own screen, showing only a Display-name field and a Create button — no
 * manual internal "Persona key," no existing-persona upload controls of
 * any kind. The internal `subjectKey` is derived server-side
 * (`CreatePersona.execute`) from the display name; the client never sends
 * one. On success, navigates straight to the separate Reference Upload
 * screen (`/generate/personas/upload`) with the new persona pre-selected
 * via a query param (survives a refresh, unlike router navigation state).
 * The "Upload reference photos" link gives a normal way back to that
 * screen later for any not-yet-complete persona, without putting upload
 * controls on this screen.
 */
export function PersonasTab({ tenantId }: PersonasTabProps) {
  const { user, csrfToken } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Owner visibility (t726u): this screen never listed existing personas, and the Reference Upload selector hides personas that already have a
  // Phota profile, so the platform OWNER had no place in GENERATE to see a workspace's trained personas. Read-only list, rendered for the
  // platform OWNER only (customers' screen is unchanged). The server already scopes GET .../personas (OWNER: every persona; others: own only).
  const isOwner = user?.isPlatformOwner === true;
  const [existing, setExisting] = useState<PersonaSummary[] | null>(null);
  const [existingError, setExistingError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    setExisting(null);
    setExistingError(null);
    api
      .get<{ personas: PersonaSummary[] }>(`/videos/${tenantId}/personas?includeInactive=true`)
      .then((result) => { if (!cancelled) setExisting(result.personas); })
      .catch((err: unknown) => { if (!cancelled) setExistingError(err instanceof ApiError ? err.message : "Could not load the personas."); });
    return () => {
      cancelled = true;
    };
  }, [isOwner, tenantId]);

  async function handleCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (user === null) {
      return;
    }
    setError(null);
    setCreating(true);
    try {
      const persona = await api.post<{ personaId: string }>(`/videos/${tenantId}/personas`, csrfToken, { displayName });
      navigate(`/generate/personas/upload?persona=${encodeURIComponent(persona.personaId)}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the persona — please try again.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div>
      <h2>Personas</h2>
      <div className="card" style={{ maxWidth: 480 }}>
        <h3>Create your model</h3>
        <p className="muted">
          Give your model a name. On the next screen, you'll upload {REQUIRED_TRAINING_IMAGE_COUNT_LABEL} clear photos of the same person:
        </p>
        <ul className="muted" style={{ marginTop: 0 }}>
          {RECOMMENDED_REFERENCE_VIEWS.map((view) => (
            <li key={view.label}>
              <strong>{view.label}</strong> — {view.description}
            </li>
          ))}
        </ul>
        {error && <div className="error-banner">{error}</div>}
        <form onSubmit={(event) => void handleCreate(event)}>
          <div className="field">
            <label htmlFor="displayName">Display name</label>
            <input id="displayName" type="text" required value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
          </div>
          <button type="submit" className="primary" disabled={creating}>
            {creating ? "Creating…" : "Create persona"}
          </button>
        </form>
      </div>
      <p className="muted">
        <a href="/generate/personas/upload">Upload reference photos</a> for a persona you've already created.
      </p>
      {isOwner && (
        <div className="card" data-testid="owner-persona-list">
          <h3>Existing personas in this workspace (owner view)</h3>
          {existingError && <div className="error-banner">{existingError}</div>}
          {existing === null && existingError === null && <div className="muted">Loading…</div>}
          {existing !== null && existing.length === 0 && <div className="muted">No personas in this workspace.</div>}
          {existing !== null && existing.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phota</th>
                  <th>Reference photos</th>
                  <th>Active</th>
                  <th>Admin status</th>
                </tr>
              </thead>
              <tbody>
                {existing.map((p) => (
                  <tr key={p.personaId}>
                    <td>{p.displayName}</td>
                    <td>{p.photaReadinessState ?? "—"}</td>
                    <td>{p.activeReferenceCount ?? "—"}</td>
                    <td>{p.active === undefined ? "—" : p.active ? "Yes" : "No"}</td>
                    <td>{p.adminStatus ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
